import type { Microsoft365MailPayload, Microsoft365MeetingPayload } from '@dbrain/shared';
import { Button, Icons } from '@dbrain/ui';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import type { ApprovalView } from '@dbrain/app-core/conversation-state';
import {
  editedMail,
  editedMeeting,
  mailEdits,
  mailFields,
  mailFooter,
  mailProblems,
  mailViewOf,
  meetingEdits,
  meetingFields,
  meetingFooter,
  meetingProblems,
  meetingViewOf,
  outcomeHeader,
  parsePayload,
  parseWriteCard,
  pendingHeader,
  wallOf,
  type MailFields,
  type MeetingFields,
  type WriteCardView,
} from '@dbrain/app-core/microsoft365-write';
import { api, isApiError } from '../../../api/client.js';
import { useCompanyZone } from '../../../api/hooks.js';
import { useMicrosoft365Status, useMicrosoft365WriteSend } from '../../../api/microsoft365.js';
import { ErrorAlert } from '../../ErrorAlert.js';
import { Microsoft365ConnectDialog } from '../ConnectDialog.js';
import { ConsentRequestButton } from '../parts.js';
import { AnsweredMailRow, MailBody } from './MailCard.js';
import { MeetingBody } from './MeetingCard.js';
import { cardPhase, featureOf, pendingReason, primaryLabel, shownOutcome, type CardPhase } from './model.js';

/**
 * A Microsoft 365 write card in the conversation (part 3, D-220…; MsSohbetOnay, MsToplantiOnay). It is drawn from the
 * canonical payload the worker stored with the approval, so what it shows is what goes out; the card stays in the
 * flow with its state once decided (no one-line resolved approval). "Gönder" / "Oluştur ve davet et" posts the
 * server's hash and the person's edits to the card's own route, once: nothing is retried, and an answer that never
 * came says so instead of offering another try. "Vazgeç" is the approval's reject. A card that cannot be read
 * exactly offers only "Vazgeç".
 */
export function Microsoft365ApprovalCard({ approval }: { approval: ApprovalView }) {
  const card = useMemo(() => parseWriteCard(approval.microsoft365), [approval.microsoft365]);
  if (!card) return <BrokenWriteCard approval={approval} />;
  return <WriteCard approval={approval} card={card} />;
}

function useReject(approval: ApprovalView) {
  const option = approval.options.find((o) => o.kind === 'reject')?.id ?? 'reject';
  return useMutation({ mutationFn: () => api('POST', `/api/approvals/${encodeURIComponent(approval.id)}`, { option }) });
}

const TONE_ICON = { done: Icons.done, failed: Icons.failed, unknown: Icons.warning, quiet: Icons.skipped } as const;

function WriteCard({ approval, card }: { approval: ApprovalView; card: WriteCardView }) {
  const { t, i18n } = useTranslation();
  const zone = useCompanyZone();
  const location = useLocation();
  const status = useMicrosoft365Status().data;
  const send = useMicrosoft365WriteSend(approval.id);
  const reject = useReject(approval);
  const [editing, setEditing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const mail = card.payload.kind === 'mail' ? card.payload : null;
  const meeting = card.payload.kind === 'meeting' ? card.payload : null;
  const [mailState, setMail] = useState<MailFields | null>(() => (mail ? mailFields(mail) : null));
  const [meetingState, setMeeting] = useState<MeetingFields | null>(() => (meeting ? meetingFields(meeting) : null));
  // address boxes holding text that was not added: such an address would not go, so "Gönder" waits
  const [typing, setTyping] = useState<Record<string, boolean>>({});
  const onPending = useCallback((box: string, pending: boolean) => setTyping((now) => (now[box] === pending ? now : { ...now, [box]: pending })), []);

  const phase: CardPhase = cardPhase({ status: approval.status, outcome: approval.microsoft365Outcome, sent: send.data ?? null, sending: send.isPending, cancelled: reject.isSuccess });
  const outcome = shownOutcome(approval.microsoft365Outcome, send.data);
  const waiting = phase === 'pending' || phase === 'sending';
  const zoneOfCard = meeting?.timeZone ?? zone;

  // what the card shows: the edited fields while it waits, the version that went out once it ended
  const sentPayload = outcome?.payload ? parsePayload(outcome.payload, card.kind) : null;
  const shown = waiting
    ? mail && mailState
      ? editedMail(mail, mailState)
      : meeting && meetingState
        ? (editedMeeting(meeting, meetingState) ?? meeting)
        : card.payload
    : (sentPayload ?? card.payload);
  const edits = mail && mailState ? mailEdits(mail, mailState) : meeting && meetingState ? meetingEdits(meeting, meetingState) : undefined;
  // the card's own text passed the server's checks; only an edit can break one
  const problems = !edits ? [] : mail && mailState ? mailProblems(mailState) : meeting && meetingState ? meetingProblems(meetingState, meeting.timeZone) : [];
  const attendees = shown.kind === 'meeting' ? shown.attendees.length : 0;

  const errorCode = send.error ? (isApiError(send.error) ? send.error.code : 'network_error') : null;
  const reason = waiting ? pendingReason(errorCode, card.kind, status, { ...(send.error && isApiError(send.error) && send.error.fields ? { fields: send.error.fields } : {}), edited: Boolean(edits) }) : null;
  const header = outcomeHeader(phase === 'sending' ? 'pending' : phase, card.kind, outcome, { t, timeZone: zoneOfCard, attendees });
  const pending = pendingHeader(card.kind, mail?.mode ?? null, t);
  const tone = header?.tone ?? 'waiting';
  const ToneIcon = header ? TONE_ICON[header.tone as keyof typeof TONE_ICON] : null;
  const editedOut = !waiting && outcome?.hash !== undefined && outcome.hash !== card.hash;

  const submit = () => {
    send.mutate({ hash: card.hash, ...(edits ? { edits } : {}) });
  };
  const revert = () => {
    if (mail) setMail(mailFields(mail));
    if (meeting) setMeeting(meetingFields(meeting));
    setEditing(false);
    // a click whose answer never came stays: going back to the card's text must not offer another send
    if (!reason?.blocksSend) send.reset();
  };
  const pick = (at: Date) => {
    if (!meeting || !meetingState) return;
    const wall = wallOf(at, meeting.timeZone);
    setMeeting({ ...meetingState, date: wall.date, start: wall.time });
  };

  const announce = header ? [header.title, header.detail].filter(Boolean).join(' ') : reason ? t(`microsoft365.chat.write.pending.${reason.key}`) : phase === 'sending' ? t(`microsoft365.chat.write.actions.${primaryLabel(card.kind, attendees, true)}`) : '';

  return (
    <div className="m365w" data-testid="m365-write" data-kind={card.kind} data-tool={card.tool} data-state={phase}>
      {/* the answered mail's row while the card waits (MsSohbetOnay); once decided the card's own "Yanıtlanan mail" names it */}
      {waiting && mail && shown.kind === 'mail' ? <AnsweredMailRow payload={shown} view={mailViewOf(card.view)} timeZone={zone} /> : null}
      <section className={`m365w-card m365w-card--${tone}`} aria-label={t(`microsoft365.chat.write.label.${card.kind}`)} aria-busy={phase === 'sending' || undefined} data-testid="m365-write-card">
        {header && ToneIcon ? (
          <div className={`m365w-head m365w-head--${header.tone}`} data-testid="m365-write-head">
            <ToneIcon size={15} className="m365w-head__icon" />
            <span className="m365w-head__text">
              <strong data-testid="m365-write-state">{header.title}</strong>
              {header.detail ? (
                <span className="m365w-head__detail" data-testid="m365-write-detail">
                  {header.detail}
                </span>
              ) : null}
            </span>
          </div>
        ) : (
          <div className="m365w-head m365w-head--waiting" data-testid="m365-write-head">
            <span className="m365w-head__dot" aria-hidden="true" />
            <strong data-testid="m365-write-state">{pending.title}</strong>
            <span>· {pending.kind}</span>
            <span className="m365w-head__spacer" />
            <span className="m365w-head__lock">
              <Icons.lock size={12} />
              {pending.lock}
            </span>
          </div>
        )}
        {shown.kind === 'mail' && mailState ? (
          <MailBody payload={shown} view={mailViewOf(card.view)} timeZone={zone} editing={waiting && editing} fields={mailState} onFields={setMail} onPending={onPending} />
        ) : shown.kind === 'meeting' && meeting && meetingState ? (
          <MeetingBody
            payload={shown as Microsoft365MeetingPayload}
            original={meeting}
            view={meetingViewOf(card.view)}
            decided={!waiting}
            editing={waiting && editing}
            fields={meetingState}
            onFields={setMeeting}
            onPending={onPending}
            {...(waiting && phase !== 'sending' ? { onPick: pick } : {})}
          />
        ) : null}
        {editedOut ? <p className="m365w-quiet m365w-quiet--edited">{t('microsoft365.chat.write.state.edited')}</p> : null}
        {reason ? (
          <div className={`m365w-reason${reason.blocksSend ? ' m365w-reason--stopped' : ''}`} data-testid="m365-write-reason" data-reason={reason.key}>
            {reason.action === 'upgrade' || reason.action === 'consent' ? <Icons.shield size={16} /> : <Icons.info size={16} />}
            <span className="m365w-reason__text">{t(`microsoft365.chat.write.pending.${reason.key}`)}</span>
            {reason.action === 'upgrade' || reason.action === 'reconnect' ? (
              <Button size="sm" variant="secondary" icon={<Icons.regenerate size={14} />} onClick={() => setConnecting(true)} data-testid="m365-write-reconnect">
                {t('microsoft365.chat.write.actions.reconnect')}
              </Button>
            ) : reason.action === 'consent' ? (
              <ConsentRequestButton />
            ) : null}
          </div>
        ) : null}
        {waiting ? <ErrorAlert error={reason ? reject.error : (send.error ?? reject.error)} /> : null}
        {waiting ? (
          <div className="m365w-foot">
            <span className="m365w-foot__text" data-testid="m365-write-footer">
              {shown.kind === 'mail' ? mailFooter(shown as Microsoft365MailPayload, t, i18n.language) : meetingFooter(shown as Microsoft365MeetingPayload, t, i18n.language)}
            </span>
            <Button variant="secondary" busy={reject.isPending} disabled={phase === 'sending'} onClick={() => reject.mutate()} data-testid="m365-write-cancel">
              {t('microsoft365.chat.write.actions.cancel')}
            </Button>
            {editing ? (
              <Button variant="secondary" disabled={phase === 'sending'} onClick={revert} data-testid="m365-write-revert">
                {t('microsoft365.chat.write.actions.stopEditing')}
              </Button>
            ) : (
              <Button variant="secondary" icon={<Icons.edit size={14} />} disabled={phase === 'sending' || reject.isPending} onClick={() => setEditing(true)} data-testid="m365-write-edit-open">
                {t('microsoft365.chat.write.actions.edit')}
              </Button>
            )}
            <Button
              icon={card.kind === 'mail' ? <Icons.send size={15} /> : <Icons.calendar size={15} />}
              busy={phase === 'sending'}
              disabled={problems.length > 0 || (editing && Object.values(typing).some(Boolean)) || reason?.blocksSend === true || reject.isPending}
              onClick={submit}
              data-testid="m365-write-send"
            >
              {t(`microsoft365.chat.write.actions.${primaryLabel(card.kind, attendees, phase === 'sending')}`)}
            </Button>
          </div>
        ) : null}
      </section>
      <span className="ui-visually-hidden" role="status" data-testid="m365-write-announce">
        {announce}
      </span>
      {connecting ? (
        <Microsoft365ConnectDialog
          returnTo={location.pathname}
          {...(reason?.action === 'upgrade' ? { features: [featureOf(card.tool)] } : {})}
          onClose={() => setConnecting(false)}
          onConnected={() => {
            setConnecting(false);
            send.reset();
          }}
        />
      ) : null}
    </div>
  );
}

/** A write card this screen cannot read exactly: never offered for sending; "Vazgeç" while it waits, its end once decided. */
function BrokenWriteCard({ approval }: { approval: ApprovalView }) {
  const { t } = useTranslation();
  const zone = useCompanyZone();
  const reject = useReject(approval);
  const kind = approval.tool.endsWith('toplanti_olustur') ? 'meeting' : 'mail';
  const phase = cardPhase({ status: approval.status, outcome: approval.microsoft365Outcome, cancelled: reject.isSuccess });
  const header = outcomeHeader(phase === 'sending' ? 'pending' : phase, kind, approval.microsoft365Outcome, { t, timeZone: zone, attendees: 1 });
  return (
    <section className={`m365w-card m365w-card--${header?.tone ?? 'waiting'}`} aria-label={t(`microsoft365.chat.write.label.${kind}`)} data-testid="m365-write-broken" data-state={phase}>
      <div className={`m365w-head m365w-head--${header?.tone ?? 'waiting'}`}>
        <strong>{header?.title ?? approval.title}</strong>
      </div>
      <div className="m365w-body">
        {approval.summary ? <p className="m365w-muted">{approval.summary}</p> : null}
        <p className="m365w-warning" role="note">
          <Icons.warning size={15} />
          <span>{t('microsoft365.chat.write.broken')}</span>
        </p>
        <ErrorAlert error={reject.error} />
      </div>
      {phase === 'pending' ? (
        <div className="m365w-foot">
          <span className="m365w-foot__text" />
          <Button variant="secondary" busy={reject.isPending} onClick={() => reject.mutate()} data-testid="m365-write-cancel">
            {t('microsoft365.chat.write.actions.cancel')}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
